import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Info, UserRoundPlus } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchCampaignList } from '../../../api/campaign.api';
import { getBrandLocations, getEntityDetails } from '../../../api/entity.api';
import { getReferralDetails } from '../../../api/referral.api';
import { getAvailablePoints } from '../../../api/rewards.api';
import { getTiers } from '../../../api/tiers.api';
import CampaignCard from '../../../components/campaign/card';
import CampaignCardSkeleton from '../../../components/campaign/card-skeleton';
import ImageWithFallback from '../../../components/ImageWithFallback';
import LocationsSkeleton from '../../../components/locations-skeleton';
import MembershipCard from '../../../components/membership/card';
import MembershipCardSkeleton from '../../../components/membership/card-skeleton';
import ShareReferralDrawer from '../../../components/referral/ShareReferralDrawer';
import HeaderList from '../../../components/reward/header-list';
import { Button } from '../../../components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem
} from '../../../components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogHeader
} from '../../../components/ui/dialog';
import { Toaster } from '../../../components/ui/toaster';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltips';
import { ICampaignList } from '../../../types/campaign.types'
import { IEntityLocation } from '../../../types/entity.types'
import RewardSection from './RewardSection';

const RewardTierDetails: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const { data: entityDetail } = useQuery({
    queryKey: ['getRewardBrands', params.id],
    queryFn: () => getEntityDetails(params.id || '')
  });

  const { data: entityLocation, isLoading: locationLoading } = useQuery({
    queryKey: ['getEntityLocation', params.id],
    queryFn: () => getBrandLocations(params.id || '', { page: 1, limit: 3 }),
    enabled: !!params.id
  });

  const { data: referralDetails } = useQuery({
    queryKey: ['getReferralDetails', params.id],
    queryFn: () => getReferralDetails(params.id || ''),
    enabled: !!params.id
  });

  const { data: campaignData, isLoading: campaignLoading } = useQuery({
    queryKey: ['getCampaignListShort', params.id],
    queryFn: () =>
      fetchCampaignList({ entity_id: params.id || '', page: 1, limit: 5 }),
    enabled: !!params.id
  });

  const { data: availablePoints } = useQuery({
    queryKey: ['getAvailablePoints', params.id],
    queryFn: () => getAvailablePoints(params.id || ''),
    enabled: !!params.id,
    refetchOnWindowFocus: false
  });
  const { data: membershipTiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['getTiers', params.id],
    queryFn: () => getTiers(params.id || ''),
    enabled: !!params.id,
    refetchOnWindowFocus: false
  });
  const summary = membershipTiers?.data?.summary;
  const currentTierIndex = membershipTiers?.data?.currentTierIndex ?? 0;
  const tiers = membershipTiers?.data?.tiers ?? [];
  const higherTier = summary?.isHighestTier
    ? tiers[currentTierIndex]
    : tiers[currentTierIndex + 1];

  const brand = {
    id: params.id,
    name: entityDetail?.success ? entityDetail?.data?.entity_name : '',
    logo: entityDetail?.success ? entityDetail?.data.entity_logo_image : ''
  };

  const handleInviteFriend = () => {
    setOpenDrawer(true);
  };

  return (
    <>
      <section className="flex flex-col gap-y-8">
        <div className={'mt-4 flex items-end justify-between gap-2 px-6'}>
          <div className="flex items-start gap-2">
            <button onClick={() => navigate('/')}>
              <ChevronLeft />
            </button>
            <div className="ml-2 size-16 md:size-20">
              <ImageWithFallback
                src={brand.logo || ''}
                alt={brand.name || 'brand logo'}
              />
            </div>
          </div>

          {referralDetails?.data.active && (
            <Button
              variant={'outline'}
              onClick={() => setOpenDialog(true)}
              className="rounded-full border-primary px-4 py-2 text-primary hover:bg-primary hover:text-white">
              <UserRoundPlus />
              Referral Program
            </Button>
          )}
        </div>
        {tiersLoading ? (
          <div className="w-full px-2 md:max-w-[60%] xl:max-w-[40%]">
            <MembershipCardSkeleton />
          </div>
        ) : (
          membershipTiers?.data?.found && (
            <Link
              to={`/my-rewards-hub/${brand.id}/tiers`}
              className="w-full px-2 md:max-w-[60%] xl:max-w-[40%]">
              <MembershipCard
                isBrand={params.id !== 'aye'}
                brandName={
                  availablePoints?.success
                    ? availablePoints.data.membership_merchant_alias
                    : ''
                }
                rewardName={summary?.current?.tier_name || ''}
                rewardPoints={summary?.next?.earned_points ?? 0}
                rewardTotalPoints={
                  summary?.isHighestTier
                    ? '- / -'
                    : (summary?.next?.threshold ?? 0)
                }
                themeColor={summary?.current?.color_hex || '#FE724C'}
                valueApplied={(summary?.current?.value_applied ?? 0).toString()}
                steppedCreditPerRm={
                  summary?.current?.stepped_credit_per_rm ?? 0
                }
                isCurrentTier={true}
                hideStatusPill={true}
                rewardAvailablePoints={
                  availablePoints?.success && availablePoints.data.points
                    ? availablePoints.data.points
                    : 0
                }
                description={
                  higherTier?.description ||
                  (params.id === 'aye'
                    ? `Earn points when you pay with AyeFace`
                    : `Earn ${
                        availablePoints?.success
                          ? availablePoints.data.membership_merchant_alias
                          : 'points'
                      } when you shop at ${brand.name}`)
                }
                currencyCode={
                  membershipTiers?.data?.fiat_currency_code ?? undefined
                }
              />
            </Link>
          )
        )}

        {process.env.REACT_APP_REWARDS_ENABLED === 'true' && (
          <RewardSection
            id={brand.id || ''}
            merchantName={brand.name || ''}
            currentTierColor={summary?.current?.color_hex || ''}
          />
        )}

        <div className="mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-y-4">
          <HeaderList
            title="Active Campaigns"
            href={`/my-rewards-hub/${brand.id}/campaigns`}
            description={undefined}
            hideSeeAll={
              !(campaignData?.success && campaignData?.data?.length > 0)
            }
          />
          {campaignLoading ? (
            <Carousel
              opts={{
                align: 'start',
              }}
              className="z-20 max-w-full bg-inherit">
              <CarouselContent className={'items-stretch gap-2 pl-2'}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <CarouselItem
                    key={`skeleton-${index}`}
                    className={`basis-auto scroll-pl-6 shadow-none drop-shadow-none md:basis-[65%] lg:basis-1/5`}>
                    <CampaignCardSkeleton className={''} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : campaignData?.success && campaignData?.data?.length > 0 ? (
            <Carousel
              opts={{
                align: 'start',
              }}
              className="z-20 max-w-full bg-inherit">
              <CarouselContent className={'items-stretch gap-2 pl-2'}>
                {campaignData.data.map(
                  (campaign: ICampaignList, index: number) => (
                    <CarouselItem
                      key={index}
                      className={`basis-auto scroll-pl-6 shadow-none drop-shadow-none md:basis-[65%] lg:basis-1/5`}>
                      <Link
                        to={`/my-rewards-hub/${brand.id}/campaigns/${campaign.campaign_id}`}>
                        <CampaignCard
                          campaign={{
                            campaignId: campaign.campaign_id,
                            campaignImageUrl: campaign.portal_image,
                            title: campaign.campaign_name,
                            description: campaign.campaign_description,
                          }}
                          className={''}
                        />
                      </Link>
                    </CarouselItem>
                  ),
                )}
              </CarouselContent>
            </Carousel>
          ) : (
            <div className="flex h-32 items-center justify-center text-gray-400">
              No active campaigns at the moment.
            </div>
          )}
        </div>

        {(locationLoading ||
          (entityLocation?.success && entityLocation?.data?.length > 0)) && (
          <div className="mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-y-4 pb-4">
            <HeaderList
              title="Locations"
              href={`/my-rewards-hub/${brand.id}/locations`}
              description={`Find ${brand.name}'s stores near you that accept AyeFace`}
            />
            {locationLoading ? (
              <LocationsSkeleton />
            ) : (
              <div className="divide-y divide-gray-300 px-2">
                {entityLocation?.success &&
                  entityLocation?.data?.length > 0 &&
                  entityLocation?.data?.map((branch: IEntityLocation) => (
                    <div
                      key={branch.entity_id}
                      className="py-4 first:pt-0 last:pb-0">
                      <h4 className="text-md font-medium">
                        {branch.entity_name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {[
                          branch.entity_address,
                          branch.entity_zipcode,
                          branch.entity_state,
                          branch.entity_country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </section>

      <ShareReferralDrawer
        open={openDrawer}
        onOpenChange={setOpenDrawer}
        referralLink={referralDetails?.data?.referral_link || ''}
        customMessage={referralDetails?.data?.custom_message}
        rewardValue={
          referralDetails?.data?.refereeAmount &&
          referralDetails?.data?.refereeRewardType
            ? `${referralDetails.data.refereeAmount} ${referralDetails.data.refereeRewardType}`
            : undefined
        }
        expiryDate={referralDetails?.data?.referral_period_end}
      />

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          <DialogHeader>
            <div className="mx-6 mt-6 flex flex-col gap-1 rounded-2xl border-2 py-4 shadow-lg shadow-black/20">
              <div className="mx-auto flex items-center gap-2">
                My successful referrals
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 cursor-help text-gray-500" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="w-56 bg-[#F3F3F3]">
                      <p className="text-xs font-extralight text-[#101B2C]">
                        Referral rewards will only be issued after the referrals
                        have completed at least a transaction with AyeFace at
                        any of this brand's outlets
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-center font-bold text-primary">
                {referralDetails?.data?.successful_ref_count}
              </p>
            </div>
          </DialogHeader>

          <img
            src="/assets/images/gift.png"
            alt={brand.name || 'gift'}
            className="mx-auto size-28"
          />

          <h3 className="text-md px-6 text-center font-light">
            Refer a friend to spend at {brand.name} and earn{' '}
            {referralDetails?.data?.refAmount}{' '}
            {referralDetails?.data?.refRewardType} when they make a purchase
          </h3>

          <Button className="mx-8" onClick={handleInviteFriend}>
            Invite a friend
          </Button>

          <p className="px-4 text-center text-xs italic text-foreground/80">
            This offer is only applicable to friends who have never made a
            payment with AyeFace at any of this brand's outlets
          </p>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  )
};

export default RewardTierDetails;
